import React, { lazy, Suspense, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { Helmet } from "react-helmet";
import { Box, CircularProgress } from "@mui/material";
import AdminLayout from "./dashboard/AdminLayout";

// Components
const ScrollToTop = lazy(() => import("./components/ScrollToTop"));
const ProtectedRoute = lazy(() =>
  import("./dashboard/pages/userlist/ProtectedRoute")
);
const Navbar = lazy(() => import("./components/Navbar"));
const Footer = lazy(() => import("./components/Footer"));
const WhatsAppButton = lazy(() => import("./components/WhatsAppButton"));
const FloatingCta = lazy(() => import("./components/FloatingCta"));

// Public Pages
const Home = lazy(() => import("./components/pages/home/Home"));
const Services = lazy(() => import("./components/pages/about-us/AboutUs"));
const Products = lazy(() => import("./components/pages/flights/Flights"));
const Holidays = lazy(() => import("./components/pages/holidays/Holidays"));
const ContactUs = lazy(() => import("./components/pages/contact-us/ContactUs"));
const Terms = lazy(() => import("./components/pages/terms/Terms"));
const PrivacyPolicy = lazy(() =>
  import("./components/pages/privacy/PrivacyPolicy")
);
const CheckOut = lazy(() => import("./components/pages/CheckOut"));
const BookFlight = lazy(() =>
  import("./components/pages/book-flight/BookFlight")
);
const BookingConfirm = lazy(() =>
  import("./components/pages/booking_confirm/BookingConfirm")
);
const FlightTo = lazy(() => import("./components/pages/flight-to/FlightTo"));

// Blog Pages
// const BlogDetails = lazy(() => import("./components/BlogDetails"));
// const BlogThailand = lazy(() => import("./components/BlogThailand"));
// const BlogDetaailsDubai = lazy(() => import("./components/BlogDetaailsDubai"));

// Auth Pages
const Login = lazy(() => import("./dashboard/pages/userlist/Login"));

// Admin Pages
const Admin = lazy(() => import("./dashboard/Admin"));
const Userlist = lazy(() => import("./dashboard/pages/userlist/Userlist"));
const Airline = lazy(() => import("./dashboard/pages/airlines/Airlines"));
const Locations = lazy(() => import("./dashboard/pages/destination/Locations"));
const Fclasses = lazy(() => import("./dashboard/pages/classes/Fclasses"));
const SeasionalFares = lazy(() =>
  import("./dashboard/pages/seasonalfares/SeasonalFares")
);
const Register = lazy(() => import("./dashboard/pages/userlist/Register"));
const AddFlight = lazy(() => import("./dashboard/pages/addflight/AddFlight"));
const PopUp = lazy(() => import("./dashboard/pages/popup/popUp"));

const LoadingFallback = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
  >
    <CircularProgress />
  </Box>
);

const PublicRouteWrapper = ({ children }) => {
  
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://embed.tawk.to/630d0d9437898912e9660784/1gblfq3bg";
    script.async = true;
    script.charset = "UTF-8";
    script.setAttribute("crossorigin", "*");
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <>
      <Suspense fallback={<LoadingFallback />}>
        <Navbar />
        {children}

        <Footer />

        <WhatsAppButton />
        <FloatingCta />
      </Suspense>
    </>
  );
};

const App = () => {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <Helmet></Helmet>

      <Router>
        <Suspense fallback={<LoadingFallback />}>
          <ScrollToTop />
          <Routes>
            {/* Public Routes */}
            <Route
              path="/"
              element={
                <PublicRouteWrapper>
                  <Home />
                </PublicRouteWrapper>
              }
            />
            <Route
              path="/about-us"
              element={
                <PublicRouteWrapper>
                  <Services />
                </PublicRouteWrapper>
              }
            />
            <Route
              path="/flights"
              element={
                <PublicRouteWrapper>
                  <Products />
                </PublicRouteWrapper>
              }
            />
            <Route
              path="/holidays"
              element={
                <PublicRouteWrapper>
                  <Holidays />
                </PublicRouteWrapper>
              }
            />
            <Route
              path="/contact-us"
              element={
                <PublicRouteWrapper>
                  <ContactUs />
                </PublicRouteWrapper>
              }
            />
            <Route
              path="/book-flight"
              element={
                <PublicRouteWrapper>
                  <BookFlight />
                </PublicRouteWrapper>
              }
            />
            <Route
              path="/flights/:destination"
              element={
                <PublicRouteWrapper>
                  <FlightTo />
                </PublicRouteWrapper>
              }
            />
            <Route
              path="/terms_conditions"
              element={
                <PublicRouteWrapper>
                  <Terms />
                </PublicRouteWrapper>
              }
            />
            <Route
              path="/privacy_policy"
              element={
                <PublicRouteWrapper>
                  <PrivacyPolicy />
                </PublicRouteWrapper>
              }
            />
            <Route
              path="/checkout"
              element={
                <PublicRouteWrapper>
                  <CheckOut />
                </PublicRouteWrapper>
              }
            />
            {/* <Route
              path="/blog-details"
              element={
                <PublicRouteWrapper>
                  <BlogDetails />
                </PublicRouteWrapper>
              }
            />
            <Route
              path="/blog-thailand"
              element={
                <PublicRouteWrapper>
                  <BlogThailand />
                </PublicRouteWrapper>
              }
            />
            <Route
              path="/blog-dubai"
              element={
                <PublicRouteWrapper>
                  <BlogDetaailsDubai />
                </PublicRouteWrapper>
              }
            /> */}
            {/* Without Header and Footer */}
            <Route path="/booking-confirm" element={<BookingConfirm />} />

            {/* Auth Routes */}
            <Route
              path="/login"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <Login />
                </Suspense>
              }
            />

            {/* Protected Admin Routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Admin />} />
              <Route path="users" element={<Userlist />} />
              <Route path="airline" element={<Airline />} />
              <Route path="locations" element={<Locations />} />
              <Route path="addflight" element={<AddFlight />} />
              <Route path="seasonal_fares" element={<SeasionalFares />} />
              <Route path="pop_up" element={<PopUp />} />
              <Route path="f_classes" element={<Fclasses />} />
              <Route path="register" element={<Register />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </Router>
    </Box>
  );
};

export default React.memo(App);
